import React, { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import navigation from "../_nav";
import { useAxios } from "../http-client/AxiosContext";
import { filterNavByScopes } from "../auth/navFilter";
import { isDoctorTier } from "../auth/scopes";
import { useMobileActionSlot } from "../hooks/MobileActionContext";

// How many copies of the tab list we render to fake an infinite loop. The middle
// copy is the "real" one — we keep the scroll position inside it and wrap by one
// copy-width whenever it drifts into a neighbour, so the list scrolls endlessly.
const SETS = 3;

// Glass bottom nav for phones/tablets — a floating rounded-pill bar of horizontally
// scrollable tab pills (active = glassy pill, centered + infinite-looping). The
// page's primary action sits OUTSIDE the pill as a separate floating circle at the
// right; the pill shortens to make room for it.
const BottomNav = () => {
  const router = useRouter();
  const { grantedScopes } = useAxios();
  const navRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLAnchorElement>(null);
  const action = useMobileActionSlot();

  const navItems = filterNavByScopes(navigation, grantedScopes)
    // A nav GROUP (e.g. Inventario) has no `to` of its own — the flat mobile bar
    // can't nest, so collapse it to a single tab linking to its first visible
    // child, and light it up on any route under the shared base.
    .map((item) =>
      item.items?.length
        ? { ...item, to: item.items[0].to, activeBase: "/inventory" }
        : item,
    )
    // "Inicio" (the print-ticket home) is dropped from the mobile tab bar; it
    // stays in the desktop sidebar.
    .filter((item) => item.to && item.to !== "/")
    .map((item) =>
      isDoctorTier(grantedScopes) && item.to === "/doctor"
        ? { ...item, name: "Mi cuenta" }
        : item,
    );
  const n = navItems.length;
  // The infinite-loop trick renders multiple copies of the list. It only reads
  // as one looping strip when a SINGLE copy overflows the bar; on a screen wide
  // enough to show a whole copy (screen width > list width — e.g. a tablet still
  // below the lg breakpoint), the extra copies show up as duplicate tabs. So we
  // only multiply when the single copy actually overflows (measured below).
  const [looped, setLooped] = useState(false);
  const sets = looped ? SETS : 1;
  const loopItems = n ? Array.from({ length: sets }, () => navItems).flat() : [];

  const isActive = (to: string) =>
    to === "/" ? router.pathname === "/" : router.pathname.startsWith(to);

  // Retain the last action so the FAB can animate OUT (cell-split morph) after
  // `action` clears, instead of unmounting instantly.
  const [shownAction, setShownAction] = useState(action);
  useEffect(() => {
    if (action) setShownAction(action);
  }, [action]);

  // The pill width tweens when the action circle appears/disappears (presence of
  // `action` toggles right-18 ↔ right-3). Animating a backdrop-filter's size
  // ghosts the blur, so we drop the glass blur (→ solid) + hide the edge frost
  // for the duration of the width tween, then restore them.
  const hasAction = !!action;
  const [animating, setAnimating] = useState(false);
  const prevHasAction = useRef(hasAction);
  useEffect(() => {
    if (prevHasAction.current === hasAction) return;
    prevHasAction.current = hasAction;
    setAnimating(true);
    const t = setTimeout(() => setAnimating(false), 320);
    return () => clearTimeout(t);
  }, [hasAction]);

  // Decide whether the looping copies are needed: only when a single copy
  // overflows the visible bar. scrollWidth/sets is the intrinsic one-copy width
  // regardless of how many copies are rendered right now, so the decision is
  // stable and won't oscillate at the threshold. Re-checks on resize (rotation,
  // window changes, scope-driven item count changes).
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || n === 0) return;
    const measure = () => {
      const oneSet = nav.scrollWidth / sets;
      setLooped(oneSet > nav.clientWidth + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [n, sets]);

  // Keep the scroll inside the middle copy so it loops seamlessly: the copies are
  // identical, so a ±copy-width jump is invisible. No-op when not looped (a
  // single copy that fits has nothing to wrap).
  const wrap = useCallback(() => {
    const nav = navRef.current;
    if (!nav || n === 0 || !looped) return;
    const set = nav.scrollWidth / SETS;
    if (set <= 0) return;
    if (nav.scrollLeft < set) nav.scrollLeft += set;
    else if (nav.scrollLeft >= set * 2) nav.scrollLeft -= set;
  }, [n, looped]);

  // Center the active tab (the middle-copy instance) within the nav's visible
  // width when the route changes. Deferred to after layout AND webfont load: tab
  // offsets shift as the font swaps in, so running at mount centers against stale
  // widths.
  useEffect(() => {
    const center = () => {
      const el = activeRef.current;
      const nav = navRef.current;
      if (!el || !nav) return;
      nav.scrollLeft = el.offsetLeft + el.offsetWidth / 2 - nav.clientWidth / 2;
    };
    const raf = requestAnimationFrame(() => requestAnimationFrame(center));
    document.fonts?.ready.then(center).catch(() => {});
    // Late fallback: scopes load + glass reflow can shift tab widths after the
    // first frames, leaving the center a few px off; re-run once it's settled.
    const t = setTimeout(center, 400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [router.pathname, n, action, looped]);

  return (
    <>
      {/* The floating tab pill — right edge stops short when an action circle is
          present, so the circle sits outside it. Outer = glass pill (clips to the
          rounded shape); inner = the scroller; the two edge overlays frost the
          tabs as they pass the left/right inner border. */}
      <nav
        className={
          // Width tweens; the glass blur is swapped to a solid surface WHILE it
          // tweens (animating a backdrop-filter's size ghosts the blur), then
          // restored. See the `animating` effect above.
          "tw:fixed tw:z-[60] tw:bottom-3 tw:left-3 tw:rounded-full tw:overflow-hidden tw:lg:hidden tw:transition-[right] tw:duration-300 tw:ease-out " +
          (animating ? "tw:glass-solid " : "tw:glass ") +
          (action ? "tw:right-18" : "tw:right-3")
        }
      >
        <div
          ref={navRef}
          onScroll={wrap}
          className="tw:relative tw:flex tw:items-center tw:gap-1 tw:overflow-x-auto tw:overscroll-x-contain tw:[-webkit-overflow-scrolling:touch] tw:px-2 tw:py-1.5 tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden"
        >
        {loopItems.map((item, i) => {
          const to = item.to as string;
          const active = isActive(((item as { activeBase?: string }).activeBase) ?? to);
          // Attach the centering ref to the active tab in the MIDDLE copy.
          const isMiddleActive = active && i >= n && i < n * 2;
          return (
            <Link
              key={`${i}-${to}`}
              href={to}
              ref={isMiddleActive ? activeRef : undefined}
              // `!` on the color: Bootstrap's unlayered `a { color }` otherwise wins
              // over our layered tw: color utility.
              className={
                "tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:rounded-full tw:px-4 tw:py-2 tw:no-underline " +
                (active
                  ? "tw:glass tw:text-accent!"
                  : "tw:text-text-muted! tw:hover:text-text! tw:hover:glass-hover")
              }
            >
              <span className="tw:flex tw:h-5 tw:w-5 tw:shrink-0 tw:items-center tw:justify-center">
                {item.icon as React.ReactNode}
              </span>
              <span className="tw:whitespace-nowrap tw:text-xs tw:font-medium">
                {item.name as string}
              </span>
            </Link>
          );
        })}
        </div>

        {/* Inner glass-blur frost at the left/right border of the scroller.
            Hidden while the width tweens (its backdrop-filter would ghost too). */}
        <span
          aria-hidden
          className={
            "tw:pointer-events-none tw:absolute tw:inset-y-0 tw:left-0 tw:w-8 tw:edge-blur-left tw:transition-opacity tw:duration-150 " +
            (animating || !looped ? "tw:opacity-0" : "tw:opacity-100")
          }
        />
        <span
          aria-hidden
          className={
            "tw:pointer-events-none tw:absolute tw:inset-y-0 tw:right-0 tw:w-8 tw:edge-blur-right tw:transition-opacity tw:duration-150 " +
            (animating || !looped ? "tw:opacity-0" : "tw:opacity-100")
          }
        />
      </nav>

      {/* Page primary action — a glassy accent CIRCLE that buds out of the pill's
          right end (cell-split morph): it scales from the pill edge with an
          overshoot when it appears, and collapses back into the pill when it
          clears. Kept mounted via shownAction so the exit can animate. */}
      {shownAction && (
        <button
          type="button"
          aria-label={shownAction.label}
          aria-hidden={!action}
          tabIndex={action ? 0 : -1}
          onClick={shownAction.onClick}
          className={
            "tw:fixed tw:z-[60] tw:bottom-3 tw:right-3 tw:flex tw:h-12 tw:w-12 tw:cursor-pointer tw:items-center tw:justify-center tw:appearance-none tw:rounded-full! tw:border tw:border-accent/40 tw:bg-accent/15! tw:text-accent! tw:shadow-lg tw:backdrop-blur-md tw:lg:hidden tw:[&_svg]:fill-current tw:origin-left tw:transition-all tw:duration-300 tw:ease-[cubic-bezier(0.34,1.56,0.64,1)] " +
            (action
              ? "tw:scale-100 tw:translate-x-0 tw:opacity-100"
              : "tw:scale-0 tw:-translate-x-7 tw:opacity-0 tw:pointer-events-none")
          }
        >
          {shownAction.icon}
        </button>
      )}
    </>
  );
};

export default BottomNav;
